import { formatDate } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { DataFormChangeEvent, DataFormEvent, DataFormInitInputs, DataFormModel, DataFormSubmitAction, DataFormSubmitResponse, DataFormValidators, DataFormValue } from '@shared/components/data-form/data-form.model';
import { DataFormService } from '@shared/components/data-form/data-form.service';
import { PageLayoutService } from '@shared/components/page-layout/page-layout.service';
import { ParentDrawerUploadResponse } from '@shared/components/page-layout/page-layout.types';
import { emptyGuid } from '@shared/constants/bringpro-constants';
import { ChargeType } from '@shared/enums/charge-type.enum';
import { CouponType } from '@shared/enums/coupon-type.enum';
import { PricingModel } from '@shared/enums/pricing-model.enum';
import { StorageType, StorageTypeValidations } from '@shared/enums/storage-type.enum';
import { TenderType } from '@shared/enums/tender-type.enum';
import { ApiExceptionErrorBody, DataFormSubmitHttpError, ModelStateErrorEntry } from '@shared/models/domain/custom-views.model';
import { ProjectView, SubscriptionView, UpchargesView } from '@shared/models/domain/domain-view.model';
import { Project, StorageItemInstance } from '@shared/models/domain/wms.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { SelectOption } from '@shared/models/shared/select-option.model';
import { AdminProjectFolderService, AdminProjectViewService, AdminUserProjectViewService } from '@shared/services/admin/admin-warehouse.service';
import { AlertService } from '@shared/services/alert.service';
import { CustomerUserCreditCardService, UserCreditCardService } from '@shared/services/credit-card.service';
import { JobItemOptionService } from '@shared/services/job.service';
import { MediaService } from '@shared/services/media.service';
import { WarehouseLocationService } from '@shared/services/warehouse.service';
import { EMPTY, forkJoin, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, mergeMap, pairwise, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-data-form',
    templateUrl: './data-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        FormlyModule,
        MatButton,
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
    ],
})
export class DataFormComponent {
    private formService = inject(DataFormService);
    private mediaService = inject(MediaService);
    private alertService = inject(AlertService);
    private authService = inject(AuthService);
    private router = inject(Router);
    private layoutService = inject(PageLayoutService);
    private adminProjectFolderService = inject(AdminProjectFolderService);
    private adminProjectViewService = inject(AdminProjectViewService);
    private adminUserProjectViewService = inject(AdminUserProjectViewService);
    private jobItemOptionService = inject(JobItemOptionService);
    private warehouseLocationService = inject(WarehouseLocationService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly dataForm = viewChild<FormGroupDirective>('dataForm');
    private readonly topDiv = viewChild<ElementRef<HTMLDivElement>>('topDiv');

    readonly formUrl = input<string>();
    readonly formFields = input<FormlyFieldConfig[]>();
    readonly submitActions = input<DataFormSubmitAction[]>();
    readonly apiUrl = input<string>();
    readonly model = input<DataFormModel | object | null>();
    readonly dictionary = input<Record<string, SelectOption<string | number | object>[]>>();
    readonly validators = input<DataFormValidators>();
    readonly idField = input<string>();
    readonly hideCancel = input(false);
    readonly readOnly = input(false);
    readonly hideSubmit = input(false);
    readonly submitLabel = input('Save');
    readonly submitAnotherLabel = input('Add');
    readonly skipApi = input(false);
    readonly addExtension = input(true);
    readonly title = input<string>();
    readonly description = input<string>();
    readonly buttonPositionAtEnd = input(false);
    readonly keepOpen = input(false);
    readonly keepOpenWithoutReset = input(false);
    readonly includeFieldsInPayload = input<string[]>([]);
    readonly hideSubmitButton = input(true);
    readonly submitForm = output<DataFormEvent>();
    readonly cancelForm = output<boolean>();
    readonly formChange = output<DataFormChangeEvent>();

    readonly dataModel = signal<DataFormModel>(undefined);
    readonly form = signal<FormGroup>(undefined);
    readonly options = signal<FormlyFormOptions>(undefined);
    readonly fields = signal<FormlyFieldConfig[]>(undefined);
    readonly isDisabled = signal(false);
    readonly isUploading = computed(() => this.formService.isUploading());

    private readonly currentModel = linkedSignal<DataFormModel>(() => this.model() as DataFormModel);
    private event: DataFormEvent;
    private originalModel: DataFormModel;
    private defaultFields: FormlyFieldConfig[];
    private previousInputs: DataFormInitInputs = { model: undefined, formUrl: undefined, formFields: undefined };
    private formValueChanges = Subscription.EMPTY;
    private isAdmin = false;
    private userCreditCardService: CustomerUserCreditCardService | UserCreditCardService;

    constructor() {
        this.isAdmin = this.router.url?.toLowerCase().includes('/office');
        this.userCreditCardService = this.isAdmin ? inject(UserCreditCardService) : inject(CustomerUserCreditCardService);

        effect(() => {
            const inputs: DataFormInitInputs = {
                model: this.model() as DataFormModel,
                formUrl: this.formUrl(),
                formFields: this.formFields(),
            };
            untracked(() => this.applyInputChanges(inputs));
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    onSubmit(keepOpen = false, submitAction: DataFormSubmitAction = null): void {
        const dataModel = this.dataModel();
        if (!this.form().valid) {
            this.form().markAllAsTouched();
            this.getFormValidationErrors();
            const questionFormUrls = ["subscription-event-complete", "invoice-approve", "quote-approve", "driver-job-checkout", "confirm-job", "storage-agreement"];
            if (questionFormUrls.includes(this.formUrl())) this.formService.isInvalidQuestionForm.set(true);
            this.alertService.warnToast("Please fix these fields to continue", "Form is invalid")
        } else {
            if (this.formUrl() && this.formUrl().startsWith('website-settings') && dataModel['settingsValue']) {
                dataModel['settingsValue'] = dataModel['settingsValue'].toString();
            }
            if (this.formUrl() === 'admin-event-action') dataModel.columnsAttachment = (dataModel.columnsAttachment as DataFormValue[])?.join(",");
            if (this.formUrl() === "admin-project") {
                dataModel.coverageValue = dataModel?.coverageValue || 0;
                const dateFields = ['scheduledDate', 'scheduledEndDate'];
                dateFields.forEach(field => {
                    if (dataModel?.[field]) {
                        dataModel[field] = formatDate(dataModel[field] as string | number | Date, 'yyyy-MM-dd', 'en-US');
                    }
                });
                const { currentStorageType, storageType, activeItems, currentFlatRate, flatRate } = dataModel;
                const isFlatRate = currentStorageType === StorageType.FlatRate;
                if ((isFlatRate && storageType !== StorageType.FlatRate && activeItems) || (isFlatRate && currentFlatRate !== flatRate)) {
                    this.alertService.confirm("Are you sure? ", "Making changes to an active sidemark with flat rate fee can result in unexpected results", "Confirm").then(confirm => {
                        if (confirm.isConfirmed) {
                            this.handleStorageTypeAsFlatRate(keepOpen, submitAction);
                        }
                    });
                } else {
                    this.handleStorageTypeAsFlatRate(keepOpen, submitAction);
                }
            }
            else {
                this.handleStorageTypeAsFlatRate(keepOpen, submitAction);
            }
        }
    }

    onCancel(): void {
        if (this.formUrl() === "project-folder" && this.currentModel()?.addRoom) {
            this.layoutService.setParentDrawerSubject({
                formUrl: this.formUrl(),
                model: { addRoomDrawerClose: true, addItemDraftModel: this.currentModel()?.addItemDraftModel ?? null }
            });
        }
        this.currentModel.set(null);
        this.originalModel = null;
        this.dataModel.set(null);
        const form = new FormGroup({});
        this.form.set(form);
        form.reset();
        this.dataForm()?.reset();
        this.options().resetModel();
        this.cancelForm.emit(true);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private applyInputChanges(inputs: DataFormInitInputs): void {
        const previous = this.previousInputs;
        this.previousInputs = inputs;
        const modelChanged = inputs.model !== previous.model && !!inputs.model;
        const formUrlChanged = inputs.formUrl !== previous.formUrl && !!inputs.formUrl;
        const formFieldsChanged = inputs.formFields !== previous.formFields && !!inputs.formFields;

        if (modelChanged) {
            this.dataModel.set(JSON.parse(JSON.stringify(inputs.model)) as DataFormModel);
            this.originalModel = JSON.parse(JSON.stringify(inputs.model)) as DataFormModel;
        }
        if (formUrlChanged && !this.form()) {
            this.formService.loadForm(inputs.formUrl, this.addExtension())
                .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
                .subscribe((fields) => {
                    if (fields) {
                        if (inputs.formUrl === 'user-profile-password') {
                            fields[0].validators = {
                                validation: [
                                    { name: 'fieldMatch', options: { errorPath: 'passwordConfirm' } },
                                ]
                            };
                        }

                        this.defaultFields = fields;
                        this.initForm(fields);

                        const settingJson = [
                            'website-settings/admin-website-settings-address',
                            'website-settings/admin-website-settings-boolean',
                            'website-settings/admin-website-settings-color',
                            'website-settings/admin-website-settings-datetime',
                            'website-settings/admin-website-settings-integer',
                            'website-settings/admin-website-settings-job-type-array',
                            'website-settings/admin-website-settings-js',
                            'website-settings/admin-website-settings-photo-upload',
                            'website-settings/admin-website-settings-string',
                            'website-settings/admin-website-settings-text',
                        ]

                        const model = this.currentModel();
                        if (model && model[this.idField()] && settingJson?.findIndex(rows => rows === inputs.formUrl)) {
                            if (fields[0]?.fieldGroup[0]?.key === 'settingsId' && fields[0].fieldGroup[0].props) {
                                fields[0].fieldGroup[0].props.readonly = true;
                                fields[0].fieldGroup[0].props.disabled = true;
                            }
                        }
                    }

                });
        }

        if (formFieldsChanged) {
            this.defaultFields = inputs.formFields;
            this.initForm(inputs.formFields);
        }

        if (modelChanged) {
            if (this.defaultFields) {
                this.initForm(this.defaultFields);
            }
        }
    }

    private getFormValidationErrors(): void {
        const form = this.form();
        Object.keys(form.controls).forEach(key => {
            const controlErrors: ValidationErrors = form.get(key).errors;
            if (controlErrors != null) {
                Object.keys(controlErrors).forEach(keyError => {
                    console.warn(`Form control "${key}" failed validation: ${keyError}`);
                });
            }
        });
    }

    private handleStorageTypeAsFlatRate(keepOpen: boolean, submitAction: DataFormSubmitAction): void {
        let request: Observable<DataFormSubmitResponse | DataFormSubmitResponse[]>;
        const dataModel = this.dataModel();
        const parsedModel = JSON.parse(JSON.stringify(dataModel)) as DataFormModel;
        if (this.skipApi()) {
            request = of({ item: parsedModel });
        }
        else {
            Object.keys(dataModel).forEach(key => {
                const value = dataModel[key];
                const field = this.scanAllForm(this.fields(), key, false);
                if (key !== 'mediaID' && Array.isArray(value) && field?.type === 'uploadFile' && field?.props?.multiple !== true) {
                    dataModel[key] = value?.length > 0 ? value[0] : null;
                }
            });

            if (dataModel['mediaID'] && Array.isArray(dataModel['mediaID'])) {
                const requests: Observable<DataFormSubmitResponse>[] = [];

                dataModel['mediaID'].forEach((mediaId) => {
                    const model = { ...dataModel };
                    model['mediaID'] = mediaId;
                    requests.push(this.ProcessApiUrl(model));
                });

                request = forkJoin(requests);
            }
            else {
                request = this.ProcessApiUrl(dataModel);
            }
        }
        this.isDisabled.set(true);
        request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data: DataFormSubmitResponse) => {
                if (data?.body && data?.body?.type === "application/msword") {
                    let fileName = '';
                    const contentDisposition = data.headers.get('Content-Disposition');
                    if (contentDisposition) {
                        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        const matches = fileNameRegex.exec(contentDisposition);
                        if (matches?.[1]) {
                            fileName = matches[1].replace(/['"]/g, '');
                        }
                    }

                    const a = document.createElement('a');
                    const objectUrl = URL.createObjectURL(data.body);
                    a.href = objectUrl;
                    a.download = fileName;
                    a.click();
                    URL.revokeObjectURL(objectUrl);

                    const event: DataFormEvent = {
                        formUrl: this.formUrl(),
                        originalData: this.originalModel,
                        currentData: null,
                        dirty: this.form().dirty,
                        model: parsedModel,
                        keepOpen: keepOpen,
                        keepOpenWithoutReset: this.keepOpenWithoutReset(),
                        hideSubmitButton: this.hideSubmitButton()
                    };
                    this.submitForm.emit(event);
                    this.formService.onFormEvent$.emit(event);
                } else {
                    this.event = {
                        formUrl: this.formUrl(),
                        originalData: this.originalModel,
                        currentData: data.item,
                        dirty: this.form().dirty,
                        model: parsedModel,
                        keepOpen: keepOpen,
                        keepOpenWithoutReset: this.keepOpenWithoutReset(),
                        hideSubmitButton: this.hideSubmitButton(),
                        submitAction: submitAction
                    };
                    this.submitForm.emit(this.event);
                    this.formService.onFormEvent$.emit(this.event);
                }

                if (!this.event?.['instanceItemFormNotReset'] && !this.keepOpenWithoutReset()) {
                    const isAddAnotherArrivalItem = keepOpen && this.formUrl() === "add-item-to-warehouse-arrival";
                    this.currentModel.set({});
                    this.form().reset();
                    this.dataForm()?.reset();
                    this.options().resetModel(isAddAnotherArrivalItem ? (this.model() as DataFormModel ?? {}) : this.currentModel());
                    if (isAddAnotherArrivalItem) {
                        this.form().controls?.projectFolderId?.setValue(this.event?.model?.projectFolderId);
                    }
                }
                this.isDisabled.set(false);

                if (this.keepOpen()) {
                    this.topDiv()?.nativeElement?.scrollIntoView();
                    this.dataForm()?.form.markAsPristine();
                    this.dataForm()?.form.markAsUntouched();
                    this.dataForm()?.form.updateValueAndValidity();
                    this.form().markAsPristine();
                    this.form().markAsUntouched();
                    this.form().updateValueAndValidity();
                }

            }, error: (error: DataFormSubmitHttpError) => {
                if (error?.status === HttpStatusCode.BadRequest) {
                    let errorMessage = 'Something went wrong.';
                    try {
                        const messageJson = error?.error?.exceptionMessage;
                        const messageObject = JSON.parse(messageJson) as Record<string, ModelStateErrorEntry>;
                        Object.keys(messageObject).every(key => {
                            const value = messageObject[key];
                            const errors = value?.Errors;
                            if (errors && Array.isArray(errors) && errors.length > 0) {
                                errorMessage = errors[0]?.ErrorMessage;
                                return false;
                            }
                            return true;
                        });
                    }
                    catch {
                        errorMessage = 'Something went wrong.';
                    }

                    this.alertService.errorToast('Error!', errorMessage);
                } else {
                    this.alertService.errorToast('Error!', (error?.error as ApiExceptionErrorBody)?.ExceptionMessage ?? 'Something went wrong.');
                }
                this.isDisabled.set(false);
            }
        });
    }

    private initForm(fields: FormlyFieldConfig[]): void {
        const form = new FormGroup({});
        this.form.set(form);
        this.options.set({ formState: { canViewItemValue: this.authService.canViewItemValue } });
        this.fields.set(fields);
        if (fields[0]?.fieldGroup?.length) {
            fields[0]?.fieldGroup[0]?.fieldGroup?.map((rows) => {
                if (this.readOnly()) {
                    rows.props.readonly = true;
                    rows.props.disabled = true;
                }
                return rows;
            });
        }

        let fieldGroups = [] as FormlyFieldConfig[];
        fields.forEach(field => {
            field.fieldGroup.forEach(element => {
                if (element.fieldGroup) {
                    fieldGroups = fieldGroups.concat(element.fieldGroup);
                }
                else {
                    fieldGroups.push(element);
                }
            });
        });

        const title = fieldGroups?.find(f => f.key === 'title');
        if (title &&
            (
                this.formUrl() !== 'invoice-line-item'
                && this.formUrl() !== 'invoice-discount-line-item'
            )
        ) {
            title.hooks = {
                onInit: ((field) => {
                    field.formControl.disable({ emitEvent: false });
                    return this.fieldHookSubscription(this.mediaService.mediasUploaded$, (res: ParentDrawerUploadResponse) => {
                        if (res?.medias?.length > 0) {
                            field.formControl.setValue(res?.medias[0].title);
                            field.formControl.enable({ emitEvent: false });
                        }
                    });
                })
            }
        }

        const reasonField = fieldGroups?.find(f => f.key === 'reasonId');
        const otherReasonField = fieldGroups?.find(f => f.key === 'otherReason');
        if (reasonField) {
            reasonField.hooks = {
                onInit: ((field) => {
                    return this.fieldHookSubscription(field.formControl.valueChanges, (value) => {
                            if (value === 'other') {
                                otherReasonField.className = 'w-full';
                                otherReasonField.formControl.setValidators([Validators.required]);
                                otherReasonField.formControl.updateValueAndValidity();
                            } else {
                                otherReasonField.className = 'w-full hidden';
                                otherReasonField.formControl.setValidators([]);
                                otherReasonField.formControl.clearValidators();
                                otherReasonField.formControl.updateValueAndValidity();
                            }
                        });
                })
            }
        }

        const carrierProviderFields = fieldGroups?.filter(f => typeof f.key === 'string' && f.key.endsWith('CarrierProviderId'));
        carrierProviderFields?.forEach((carrierField) => {
            const flagKey = (carrierField.key as string).replace(/CarrierProviderId$/, 'CarrierIsUnknown');
            const applyCarrierIsUnknown = (value: string): void => {
                const fieldOptions = carrierField.props?.options ?? [];
                const selected = (fieldOptions as SelectOption[]).find((o) => o.value === value);
                this.dataModel()[flagKey] = (selected?.label ?? '').trim().toLowerCase() === 'unknown';
                carrierField.options.detectChanges(carrierField.parent ?? carrierField);
            };
            carrierField.hooks = {
                onInit: ((field) => {
                    applyCarrierIsUnknown(field.formControl.value as string);
                    return this.fieldHookSubscription(field.formControl.valueChanges, (value: string) => applyCarrierIsUnknown(value));
                })
            };
        });

        const useAfterShipField = fieldGroups?.find(f => f.key === 'useAfterShip');
        const afterShipSlugField = fieldGroups?.find(f => f.key === 'afterShipSlug');
        if (useAfterShipField && afterShipSlugField) {
            useAfterShipField.hooks = {
                onInit: ((field) => {
                    return this.fieldHookSubscription(field.formControl.valueChanges, (value) => {
                            if (!value) {
                                afterShipSlugField.formControl.setValue(null);
                                this.dataModel()['afterShipSlug'] = null;
                            }
                        });
                })
            };
        }

        this.initializePriceOverride(fieldGroups);

        const dictionary = this.dictionary();
        if (dictionary) {
            Object.keys(dictionary).forEach((key) => {
                let tempKey = key.replace('List', 'Id');
                if (tempKey === 'settingsValueId') {
                    tempKey = 'settingsValue';
                }
                const target = this.scanAllForm(fields, tempKey, false);
                if (target) {
                    const tempOptions = dictionary[key]?.map((rows) => {
                        if (!isNaN(rows.value as number)) {
                            rows.value = parseInt(rows.value as string);
                        }
                        return rows;
                    });
                    target.props.options = tempOptions;
                }
            });
        }

        this.initializeDependentFormControl(fieldGroups, 'ownerUserId', 'projectId');
        this.initializeDependentFormControl(fieldGroups, 'projectId', 'projectFolderId');
        this.initializeDependentFormControl(fieldGroups, 'warehouseId', 'warehouseLocationId');
        this.initializeDependentFormControl(fieldGroups, 'upchargeId', 'title');
        this.initializeDependentFormControl(fieldGroups, 'upchargeId', 'description');
        this.initializeDependentFormControl(fieldGroups, 'couponId', 'description');
        this.initializeDependentFormControl(fieldGroups, 'percentage', 'unitPrice');
        this.initializeDependentFormControl(fieldGroups, 'percentage', 'couponValue');

        // Add unique name validation for project-folder form
        if (this.formUrl() === 'project-folder' && (dictionary?.projectFolderId || dictionary?.roomList)) {
            const nameField = fieldGroups?.find(f => f.key === 'name');
            if (nameField) {
                const currentRoomId = this.currentModel()?.id;
                nameField.props.existingNames = (dictionary.projectFolderId || dictionary?.roomList)
                    ?.filter(f => f.value !== emptyGuid && f.value !== currentRoomId)
                    ?.map(f => f.label.toLowerCase().trim()) || [];
                nameField.validators = {
                    validation: ['uniqueName']
                };
            }
        }

        if (this.formUrl() === 'project-folder' && this.currentModel()?.selectRoom && dictionary?.projectFolderList) {
            const roomPickerField = fieldGroups?.find(f => f.key === 'projectFolderId');
            const roomNameField = fieldGroups?.find(f => f.key === 'name');
            if (roomPickerField) {
                roomPickerField.hooks = {
                    onInit: (field) => {
                        return this.fieldHookSubscription(field.formControl.valueChanges, (selectedId) => {
                                const selectedRoom = this.dictionary()?.projectFolderList?.find(o => o.value === selectedId);
                                if (selectedRoom) {
                                    this.currentModel().id = selectedRoom.value;
                                    this.dataModel().id = selectedRoom.value;
                                    roomNameField?.formControl?.setValue(selectedRoom.label);
                                }
                            });
                    }
                };
            }
        }

        if (this.formUrl() === 'move-to-folder' && dictionary?.projectFolderList) {
            const sourceField = fieldGroups?.find(f => f.key === 'sourceProjectFolderId');
            const destinationField = fieldGroups?.find(f => f.key === 'projectFolderId');
            if (sourceField && destinationField) {
                const applyFilter = (sourceId: string): void => {
                    destinationField.props.options = this.dictionary()?.projectFolderList?.filter(o => o.value !== sourceId) ?? [];
                    if (destinationField.formControl.value === sourceId) {
                        destinationField.formControl.setValue(null);
                    }
                };
                sourceField.hooks = {
                    onInit: (field) => {
                        applyFilter(field.formControl.value as string);
                        return this.fieldHookSubscription(field.formControl.valueChanges, (sourceId: string) => applyFilter(sourceId));
                    }
                };
            }
        }

        const mainFormControl = fieldGroups?.find(f => f.key === 'storageType');
        const dependentFormControl = fieldGroups?.find(f => f.key === 'depth');
        if (mainFormControl && dependentFormControl) {
            if (mainFormControl && dependentFormControl) {
                mainFormControl.hooks = {
                    onInit: ((field) => {
                        if (mainFormControl.formControl.value >= 0) {
                            update(mainFormControl.formControl.value as StorageType);
                        }

                        return this.fieldHookSubscription(field.formControl.valueChanges, (value: StorageType) => {
                                update(value);
                            });
                    })
                };

                const update = (value: StorageType): void => {
                    const byVolume = StorageTypeValidations.byVolume(value);
                    dependentFormControl.props.hidden = !byVolume;
                    dependentFormControl.props.disabled = !byVolume;
                    dependentFormControl.props.required = byVolume;
                }
            }
        }

        const validators = this.validators();
        if (validators) {
            Object.keys(validators)
                .forEach((key) => {
                    if (validators[key]?.length) {
                        const target: FormlyFieldConfig = this.scanAllForm(fields, key, false);
                        if (target) {
                            if (!target.asyncValidators) {
                                target.asyncValidators = {};
                            }
                            for (const validator of validators[key]) {
                                switch (validator) {
                                    case 'usernameUnique':
                                        this.formService.setModel = this.currentModel();
                                        target.asyncValidators[key] = {
                                            expression: this.formService.validateUniqueUsername.bind(this.formService) as DataFormService['validateUniqueUsername'],
                                            message: 'This username is already taken.',
                                        }
                                        break;

                                    case 'emailUnique':
                                        this.formService.setModel = this.currentModel();
                                        target.asyncValidators[key] = {
                                            expression: this.formService.validateUniqueEmail.bind(this.formService) as DataFormService['validateUniqueEmail'],
                                            message: 'This email is already taken.',
                                        }
                                        target.props.change = () => {
                                            const tempTarget: FormlyFieldConfig = this.scanAllForm(this.fields(), 'username', false);
                                            tempTarget.formControl.setValue(target.formControl.value);
                                        };
                                        break;

                                    case 'contactEmailUnique':
                                        this.formService.setModel = this.currentModel();
                                        target.asyncValidators[key] = {
                                            expression: this.formService.validateContactUniqueEmail.bind(this.formService) as DataFormService['validateContactUniqueEmail'],
                                            message: 'This email is already taken.',
                                        }
                                        target.props.change = () => {
                                            const tempTarget: FormlyFieldConfig = this.scanAllForm(this.fields(), key, false);
                                            tempTarget.formControl.setValue(target.formControl.value);
                                        };
                                        break;

                                    case 'ignore':
                                        if (target.asyncValidators[key]) {
                                            delete target.asyncValidators[key];
                                        }
                                        break;
                                }
                            }
                        }
                    }
                });
        }

        if (this.formUrl() === 'admin-project' && this.dataModel()) {
            this.userCreditCardService.setCreditCardData({ ...this.dataModel() } as DataFormModel & Project);
        }

        this.formValueChanges.unsubscribe();
        this.formValueChanges = form.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                let emitModel = value as DataFormModel;
                switch (this.formUrl()) {
                    case 'project':
                        this.userCreditCardService.setCreditCardData(value as DataFormModel & Project);
                        break;
                    case 'admin-project': {
                            const updatedCreditCardData = {
                                ...this.dataModel(),
                                ...Object.fromEntries(
                                    Object.entries(value as DataFormModel).filter(([, v]) => v !== null && v !== undefined)
                                )
                            } as DataFormModel & Project;
                            this.userCreditCardService.setCreditCardData(updatedCreditCardData);
                            emitModel = updatedCreditCardData;
                        }
                        break;
                    case 'add-item-to-warehouse-arrival':
                        this.jobItemOptionService.setWarehouseArrivalItem(value as DataFormModel & StorageItemInstance);
                        break;
                }
                this.formChange.emit({ model: emitModel, isValid: this.form().valid } as DataFormChangeEvent);
            });

        // Payment Terms
        const paymentTerms = fieldGroups?.find(f => f.key === "paymentTermsSideMark");
        if (paymentTerms) {
            paymentTerms.hooks = {
                onInit: ((field) => {
                    return this.fieldHookSubscription(
                        field.formControl.valueChanges.pipe(startWith([] as string[]), pairwise()),
                        ([prev, next]: [SelectOption['value'][], SelectOption['value'][]]) => {
                                const nextValueAllSelected = next?.findIndex(n => n === 'All') > -1;
                                const prevValueAllSelected = prev?.findIndex(p => p === 'All') > -1;
                                const options = this.dictionary()?.paymentTermsSideMark?.map(opt => opt.value) || [];
                                if (nextValueAllSelected && !prevValueAllSelected) {
                                    field.formControl.setValue(options, { emitEvent: false });
                                    this.dataModel()['paymentTermsSideMark'] = options;
                                }
                                else if (nextValueAllSelected && next?.length < options.length) {
                                    field.formControl.setValue(next.filter(n => n !== 'All'), { emitEvent: false });
                                    this.dataModel()['paymentTermsSideMark'] = options;
                                }
                                else if (!nextValueAllSelected && prevValueAllSelected && next?.length === options.length - 1 ) {
                                    const selectedValues = this.dictionary()?.paymentTermsSideMark?.filter((item: SelectOption<ProjectView>) => item?.extra?.tenderType === TenderType.PaymentTerms)?.map(item => item?.value) || [];
                                    field.formControl.setValue(selectedValues, { emitEvent: false });
                                    this.dataModel()['paymentTermsSideMark'] = options;
                                }
                            }
                    );
                })
            };
        }
    }

    private fieldHookSubscription<T>(source: Observable<T>, next: (value: T) => void): Observable<never> {
        return new Observable<never>(() => source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(next));
    }

    private initializeDependentFormControl(fieldGroups: FormlyFieldConfig[], mainFormControlName: string, dependentFormControlName: string): void {
        const mainFormControl = fieldGroups?.find(f => f.key === mainFormControlName);
        const dependentFormControl = fieldGroups?.find(f => f.key === dependentFormControlName);
        if (mainFormControl && dependentFormControl) {
            mainFormControl.hooks = {
                onInit: ((field) => {
                    const skuField = fieldGroups?.find(f => f.key === (this.formUrl() === 'upcharge' ? 'couponId' : 'upchargeId'));
                    let selectedCoupon = {} as UpchargesView;
                    (skuField?.props?.options as SelectOption<UpchargesView>[])?.forEach((line) => {
                        if(skuField?.formControl?.value === line.value) {
                            selectedCoupon = (line?.extra ?? line) as UpchargesView;
                        }
                    });

                    if (selectedCoupon['chargeType'] === ChargeType.Packing && skuField) {
                        skuField.formControl?.disable({ emitEvent: false });
                    }
                    if (mainFormControl?.key === 'percentage' && selectedCoupon['couponType'] === 3) {
                        mainFormControl.className = "";
                        fieldGroups.find(f => f.key === (this.formUrl() === 'upcharge' ? 'couponValue' : 'unitPrice')).className = 'hidden';
                        fieldGroups.find(f => f.key === 'percentage').formControl.setValue((this.formUrl() === 'upcharge' ? this.currentModel()?.couponValue : this.currentModel()?.unitPrice));
                        const quantityField = fieldGroups.find(f => f.key === 'quantity');
                        if (quantityField?.props) {
                            quantityField.props.readonly = true;
                            quantityField.props.disabled = true;
                        }
                    }
                    if (mainFormControl?.key === 'upchargeId') {
                        this.applyUpchargeState(fieldGroups, selectedCoupon, this.shouldApplyUpchargeDefaults(mainFormControl.formControl.value as string | number));
                    }
                    if (mainFormControl.formControl.value) {
                        if(mainFormControl?.name !== 'upchargeId' && dependentFormControl?.key !== "projectFolderId") {
                            updateList(mainFormControl.formControl.value as string | number);
                        }
                    }

                    return field.formControl.valueChanges
                        .pipe(
                            mergeMap((value) => {
                            if (mainFormControl?.key !== 'ownerUserId' && mainFormControl?.key !== 'projectId' && mainFormControl?.key !== 'upchargeId' && mainFormControl?.key !== 'percentage' && mainFormControl?.key !== "couponId" && !(mainFormControl?.key === 'warehouseId' && this.formUrl() === 'instance-item')) {
                                updateList(value as string | number);
                                return EMPTY;
                            }
                            else if (mainFormControl?.key === 'warehouseId' && this.formUrl() === 'instance-item') {
                                const warehouseLocationField = fieldGroups?.find(f => f.key === dependentFormControlName);
                                if (!warehouseLocationField || !value) return EMPTY;

                                const control = warehouseLocationField.formControl;
                                control?.reset();
                                warehouseLocationField.props.options = [];

                                if (this.currentModel()?.warehouseLocationId) {
                                    warehouseLocationField.props.required = true;
                                    control?.setValidators(Validators.required);
                                    control?.updateValueAndValidity();
                                }

                                const request = new PageRequest();
                                request.itemsPerPage = 1000;
                                request.find = {
                                    'warehouseId': value as string,
                                    'deleted': 'IS NULL'
                                };
                                request.sort = 'name';
                                request.sortDir = 'asc';

                               return this.warehouseLocationService.get(request)
                                    .pipe(
                                        switchMap(({ items }) => {
                                            warehouseLocationField.props.options = items?.map(l => ({
                                                label: l?.name,
                                                value: l?.id,
                                                extra: l
                                            })) ?? [];
                                            control?.updateValueAndValidity();
                                            return of(items);
                                        }),
                                        catchError(() => EMPTY)
                                    );
                            }
                            else if (mainFormControl?.key === 'ownerUserId') {
                                const projectField = fieldGroups?.find(f => f.key === dependentFormControlName);
                                if (projectField && (value as string)?.length > 10) {
                                    projectField.formControl.setValue(null);
                                    projectField.props.options = [];
                                    const userOptions = mainFormControl?.props?.options as SelectOption<SubscriptionView[] | SubscriptionView>[];
                                    const userExtra = userOptions?.find((option) => option.value === value)?.extra;
                                    const subscriptionData = Array.isArray(userExtra) ? userExtra[0] : userExtra;
                                    const subscriptionIdField = fieldGroups?.find(f => f.key === 'subscriptionId');
                                    if (subscriptionIdField) {
                                        subscriptionIdField.formControl.setValue(subscriptionData?.id || '');
                                    }
                                    const sidemarkRequest = new PageRequest();
                                    sidemarkRequest.currentPage = 1;
                                    sidemarkRequest.itemsPerPage = 10000;
                                    sidemarkRequest.sort = 'projectName';
                                    sidemarkRequest.sortDir = 'asc';
                                    sidemarkRequest.find = { userId: value as string, isVisible: true };
                                    return this.adminUserProjectViewService.get(sidemarkRequest).pipe(
                                        tap((response) => {
                                            const sidemarks = response?.items ?? [];
                                            if (sidemarks.length > 0) {
                                                projectField.props.options = sidemarks.map((sidemark) => {
                                                    return {
                                                        label: sidemark?.projectName,
                                                        value: sidemark?.projectId,
                                                        extra: sidemark,
                                                    };
                                                });
                                                projectField.formControl.setValue(sidemarks[0]?.projectId);
                                            }
                                        }),
                                        catchError(() => {
                                            this.alertService.warnToast('Warning', 'Could not load sidemarks for the selected customer. Please try again.');
                                            return EMPTY;
                                        })
                                    );
                                }
                                return EMPTY;
                            }
                            else if (mainFormControl?.key === 'percentage') {
                                const unitPriceField = fieldGroups?.find(f => f.key === 'unitPrice');
                                const couponValueField = fieldGroups?.find(f => f.key === 'couponValue');
                                const quantityField = fieldGroups?.find(f => f.key === 'quantity');
                                const percentageField = fieldGroups?.find(f => f.key === 'percentage');
                                const skuField = fieldGroups?.find(f => f.key === (this.formUrl() === 'upcharge' ? 'couponId' : 'upchargeId'));
                                let selectedCoupon = {} as UpchargesView;
                                (skuField?.props?.options as SelectOption<UpchargesView>[])?.forEach((line) => {
                                    if(skuField?.formControl?.value === line.value) {
                                        selectedCoupon = (line?.extra ?? line) as UpchargesView;
                                    }
                                });
                                const couponValue = percentageField?.formControl?.value as number;
                                if (couponValue && selectedCoupon['couponType'] === 3) {
                                    if (unitPriceField) {
                                        unitPriceField.className = 'hidden';
                                    }
                                    if (couponValueField) {
                                        couponValueField.className = 'hidden';
                                    }
                                    if (quantityField) {
                                        quantityField.props.readonly = true;
                                        quantityField.props.disabled = true;
                                    }
                                    const unitPrice = selectedCoupon['couponType'] === 3 ? value as number : couponValue;
                                    fieldGroups?.find(f => f.key === 'unitPrice')?.formControl?.setValue(unitPrice);
                                    fieldGroups?.find(f => f.key === 'couponValue')?.formControl?.setValue(unitPrice);
                                }
                                return EMPTY;
                            }
                            else if (mainFormControl?.key === 'upchargeId') {
                                let selectedCoupon = {} as UpchargesView;
                                (mainFormControl.props?.options as SelectOption<UpchargesView>[])?.forEach((line) => {
                                    if (value === line.value) {
                                        selectedCoupon = (line?.extra ?? line) as UpchargesView;
                                    }
                                });
                                this.applyUpchargeState(fieldGroups, selectedCoupon, this.shouldApplyUpchargeDefaults(value as string | number));
                                return EMPTY;
                            }
                            else if (mainFormControl?.key === "couponId") {
                                const percentageField = fieldGroups?.find(f => f.key === 'percentage');
                                const couponValueField = fieldGroups?.find(f => f.key === 'couponValue');
                                const quantityField = fieldGroups?.find(f => f.key === 'quantity');
                                let selectedCoupon = {} as UpchargesView;
                                (mainFormControl.props?.options as SelectOption<UpchargesView>[])?.forEach((line) => {
                                    if (value === line?.value) {
                                        selectedCoupon = (line?.extra ?? line) as UpchargesView;
                                    }
                                });
                                const couponValue = selectedCoupon['couponValue'];
                                const dataModel = this.dataModel();
                                if (this.formUrl() === 'upcharge' && dataModel && selectedCoupon['couponType']) {
                                    dataModel['couponTypeId'] = selectedCoupon['couponType'];
                                }
                                if (couponValue && selectedCoupon['couponType'] === 3 && percentageField) {
                                    percentageField.className = '';
                                    percentageField?.formControl?.setValue(selectedCoupon['couponValue'], { emitEvent: false });
                                    if (couponValueField) {
                                        couponValueField.className = 'hidden';
                                    }
                                    if (quantityField) {
                                        quantityField.props.readonly = true;
                                        quantityField.props.disabled = true;
                                    }
                                }
                                if (selectedCoupon['couponType'] !== 3) {
                                    if(percentageField) {
                                        percentageField.className = 'hidden';
                                        percentageField?.formControl?.setValue(0, { emitEvent: false });
                                        if (couponValueField) {
                                            couponValueField.className = '';
                                        }
                                        if (quantityField) {
                                            quantityField.props.readonly = false;
                                            quantityField.props.disabled = false;
                                        }
                                    }
                                }
                                fieldGroups?.find(f => f.key === 'description')?.formControl?.setValue(selectedCoupon['couponDescription']);
                                fieldGroups?.find(f => f.key === 'couponValue')?.formControl?.setValue(couponValue);
                                return EMPTY;
                            }
                            else if (mainFormControl?.key === 'projectId' && value) {
                                const projectFolderField = fieldGroups?.find(f => f.key === dependentFormControlName);
                                const request = new PageRequest();
                                request.itemsPerPage = 10000;
                                request.find = {
                                    'projectId': value as string,
                                    'deleted': 'IS NULL'
                                };
                                const defaultOption = { label: 'Default Room', value: emptyGuid };
                                const subscriptionIdField = fieldGroups?.find(f => f.key === 'subscriptionId');
                                const warehouseIdField = fieldGroups?.find(f => f.key === 'warehouseId');
                                const syncProject$ = this.formUrl() === 'add-item-to-warehouse-arrival'
                                    ? this.adminProjectViewService.getById(value as string).pipe(
                                        tap((project) => {
                                            subscriptionIdField?.formControl?.setValue(project?.subscriptionId || '');
                                            warehouseIdField?.formControl?.setValue(project?.warehouseId || '');
                                        }),
                                        catchError(() => EMPTY),
                                    )
                                    : EMPTY;
                                return merge(syncProject$, this.adminProjectFolderService.get(request).pipe(
                                    tap((response) => {
                                        if (response.items.length) {
                                            projectFolderField.props.options = [
                                                defaultOption,
                                                ...response.items.map((projectFolder) => {
                                                    return {
                                                        label: projectFolder?.name,
                                                        value: projectFolder?.id,
                                                        extra: projectFolder,
                                                    };
                                                })
                                            ];
                                        } else {
                                            projectFolderField.props.options = [defaultOption];
                                        }
                                        const folderOptions = projectFolderField.props.options as SelectOption[];
                                        const currentFolderId = projectFolderField.formControl.value as string;
                                        const hasCurrentFolder = folderOptions.some(option => option.value === currentFolderId);
                                        projectFolderField.formControl.setValue(hasCurrentFolder ? currentFolderId : emptyGuid);
                                    }),
                                    catchError(() => EMPTY)
                                ));
                            }
                            return EMPTY;
                            })
                        );
                })
            };

            const updateList = (value: string | number): void => {
                const list = dependentFormControlName.replace(/Id$/, 'List');
                dependentFormControl.props.options = this.dictionary()[list]?.filter((row: { value: string | number | boolean; label: string; extra: Record<string, string | number | boolean | null>; disabled?: boolean }) => row.extra[mainFormControlName] === value);
            }
        }
    }

    private shouldApplyUpchargeDefaults(value: string | number): boolean {
        const isExistingInvoiceLineItem = ['invoice-line-item', 'invoice-discount-line-item'].includes(this.formUrl()) && !!this.currentModel()?.id;
        return !isExistingInvoiceLineItem || (value && value !== this.currentModel()?.upchargeId);
    }

    private applyUpchargeState(fieldGroups: FormlyFieldConfig[], selectedCoupon: UpchargesView, applyDefaults = true): void {
        const unitPriceField = fieldGroups?.find(f => f.key === 'unitPrice');
        const couponValueField = fieldGroups?.find(f => f.key === 'couponValue');
        const percentageField = fieldGroups?.find(f => f.key === 'percentage');
        const quantityField = fieldGroups?.find(f => f.key === 'quantity');
        const isFirstMilePickupItem = fieldGroups?.find(f => f.key === 'firstMilePickupHint');
        const isFirstMilePickupCharge = selectedCoupon?.chargeType === ChargeType.FirstMilePickup;
        const couponValue = selectedCoupon?.['couponValue'];

        if (isFirstMilePickupItem) {
            if (isFirstMilePickupCharge) {
                isFirstMilePickupItem.className = 'pb-4';
                isFirstMilePickupItem.formControl?.setValue(
                    '<strong>First Mile Pickup (MPFMP)</strong> - When you click the Save button, you will be redirected to the Pickup Quote Form.'
                );
            } else {
                isFirstMilePickupItem.className = 'hidden';
                isFirstMilePickupItem.formControl?.setValue('');
            }
        }

        if (couponValue && selectedCoupon?.['couponType'] === CouponType.Percent && percentageField) {
            percentageField.className = '';
            if (applyDefaults) {
                percentageField?.formControl?.setValue(selectedCoupon['couponValue'], { emitEvent: false });
            }
            if (unitPriceField) {
                unitPriceField.className = 'hidden';
            }
            if (couponValueField) {
                couponValueField.className = 'hidden';
            }
            if (quantityField) {
                quantityField.className = '';
                quantityField.props.readonly = true;
                quantityField.props.disabled = true;
            }
        }

        if (selectedCoupon?.['couponType'] !== CouponType.Percent) {
            if (percentageField) {
                percentageField.className = 'hidden';
                if (applyDefaults) {
                    percentageField?.formControl?.setValue(0, { emitEvent: false });
                }

                if (unitPriceField) {
                    unitPriceField.className = isFirstMilePickupCharge ? 'hidden' : '';
                }
                if (couponValueField) {
                    couponValueField.className = '';
                }
                if (quantityField) {
                    quantityField.className = isFirstMilePickupCharge ? 'hidden' : '';
                    quantityField.props.readonly = isFirstMilePickupCharge;
                    quantityField.props.disabled = isFirstMilePickupCharge;
                }
            }
        }

        if (!applyDefaults) return;

        fieldGroups?.find(f => f.key === 'title')?.formControl?.setValue(selectedCoupon?.['couponDescription']);
        fieldGroups?.find(f => f.key === 'description')?.formControl?.setValue(selectedCoupon?.['couponDescription']);
        fieldGroups?.find(f => f.key === 'note')?.formControl?.setValue(selectedCoupon?.['couponCode']);
        fieldGroups?.find(f => f.key === 'unitPrice')?.formControl?.setValue(couponValue);
        fieldGroups?.find(f => f.key === 'couponValue')?.formControl?.setValue(couponValue);
    }

    private initializePriceOverride(fieldGroups: FormlyFieldConfig[]): void {
        const isOverrideControl = fieldGroups?.find(f => f.key === 'isOverride');
        const pricingModelControl = fieldGroups?.find(f => f.key === 'pricingModel');
        const price = fieldGroups?.find(f => f.key === 'price');
        if (isOverrideControl && price && pricingModelControl) {
            isOverrideControl.hooks = {
                onInit: ((field) => {
                    if (field.formControl.value) {
                        this.enableIsOverride(price, pricingModelControl);
                    }
                    else {
                        this.disableIsOverride(price, pricingModelControl);
                    }

                    return this.fieldHookSubscription(field.formControl.valueChanges, (value) => {
                            if (value) {
                                this.enableIsOverride(price, pricingModelControl);
                            }
                            else {
                                this.disableIsOverride(price, pricingModelControl);
                            }
                        });
                })
            };
        }
    }

    private disableIsOverride(price: FormlyFieldConfig, pricingModelControl: FormlyFieldConfig): void {
        price.formControl.disable({ emitEvent: false });
        price.formControl.setValue(null);
        price.formControl.clearValidators();
        pricingModelControl.formControl.enable({ emitEvent: false });
    }

    private enableIsOverride(price: FormlyFieldConfig, pricingModelControl: FormlyFieldConfig): void {
        price.formControl.enable({ emitEvent: false });
        if (this.isAdmin) {
            price.formControl.setValidators([Validators.min(0)]);
        } else {
            price.formControl.setValidators([Validators.required, Validators.min(1)]);
        }
        price.formControl.updateValueAndValidity({ emitEvent: false });
        pricingModelControl.formControl.setValue(PricingModel.flatRate, { emitEvent: true });
        pricingModelControl.formControl.disable({ emitEvent: false });
    }

    private scanAllForm(fields: FormlyFieldConfig[], searchKey: string, checkType: boolean): FormlyFieldConfig {
        for (const field of fields) {
            const result = this.scanForm(field, searchKey, checkType);
            if (result !== null) {
                return result;
            }
        }
        return null;
    }

    private scanForm(field: FormlyFieldConfig, searchKey: string, checkType: boolean): FormlyFieldConfig {
        if (field?.key && field.key === searchKey) {
            return field;
        } else if (checkType && field?.type && field.type === searchKey) {
            return field;
        } else {
            if (field?.fieldGroup?.length) {
                for (const childField of field.fieldGroup) {
                    const result = this.scanForm(childField, searchKey, checkType);
                    if (result !== null) {
                        return result;
                    }
                }
            }
            return null;
        }
    }

    private ProcessApiUrl(model: DataFormModel): Observable<DataFormSubmitResponse> {
        let request: Observable<DataFormSubmitResponse>;
        const idField = this.idField();
        if (idField && this.currentModel()[idField]) {
            const updatedValue = this.getUpdatedModelValue(model);
            request = this.formService.patchForm(this.apiUrl() + '/' + String(this.currentModel()[idField]), updatedValue);
        } else {
            request = this.formService.postForm(this.apiUrl(), model);
        }
        return request;
    }

    private getUpdatedModelValue(model: DataFormModel): DataFormModel {
        const originalModel = this.originalModel ?? {};
        const idField = this.idField();
        const ignoreCompareProps: string[] = ['modifiedColumns', idField];
        const updatedValue: DataFormModel = {};
        const modelProps = Object.keys(model);
        modelProps.forEach(key => {
            if (this.includeFieldsInPayload().includes(key) || (originalModel[key] !== model[key] && !ignoreCompareProps.includes(key))) {
                updatedValue[key] = model[key];
            }
        });
        updatedValue[idField] = model[idField];
        return updatedValue;
    }
}
